import heic2any from 'heic2any';

/**
 * HEIC/HEIF画像をJPEGに変換するクラス
 * heic2anyライブラリを使用してブラウザ上で変換を実行
 */
class HEICSupport {
  constructor() {
    this.conversionTimeout = 30000; // 30秒タイムアウト
    this.isExifLibLoaded = false;
    this.lastConversionMethod = null;
  }

  /**
   * EXIF抽出ライブラリを動的にロード
   */
  async loadExifLib() {
    if (this.isExifLibLoaded || window.findEXIFinHEIC) {
      this.isExifLibLoaded = true;
      return true;
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/exif-heic-js/exif-heic-js/exif-heic.min.js';
      script.onload = () => {
        this.isExifLibLoaded = true;
        console.log('[HEIC] exif-heic loaded:', {
          hasFindEXIFinHEIC: typeof window.findEXIFinHEIC === 'function',
          findEXIFinHEICType: typeof window.findEXIFinHEIC
        });
        resolve(true);
      };
      script.onerror = () => {
        console.warn('EXIF-HEIC library failed to load. EXIF might be lost during client-side conversion.');
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }

  /**
   * ファイルがHEIC/HEIF形式かどうかを判定
   * @param {File} file -チェックするファイル
   * @returns {boolean} HEIC/HEIFファイルの場合true
   */
  isHEICFile(file) {
    const heicExtensions = ['.heic', '.heif'];
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    return heicExtensions.some(ext => fileName.endsWith(ext)) ||
           fileType === 'image/heic' || 
           fileType === 'image/heif';
  }

  /**
   * HEIC/HEIF画像をJPEGに変換
   * @param {File} heicFile - 変換するHEIC/HEIFファイル
   * @param {Object} options - 変換オプション
   * @param {number} options.quality - JPEG品質 (0-1, デフォルト: 0.8)
   * @returns {Promise<File>} 変換されたJPEGファイル
   */
  async convertToJPEG(heicFile, options = {}) {
    const { quality = 0.8 } = options;

    try {
      console.log('HEIC変換を開始します:', heicFile.name);
      
      // 1. 先にEXIFを抽出しておく (heic2anyで消えてしまうため)
      let exifBinary = null;
      let exifObject = null;
      let exifStrFromObject = null;
      try {
        await this.loadExifLib();
        console.log('[HEIC] findEXIFinHEIC availability:', typeof window.findEXIFinHEIC);
        if (window.findEXIFinHEIC) {
          const buffer = await heicFile.arrayBuffer();
          exifBinary = window.findEXIFinHEIC(buffer);
          if (exifBinary && typeof exifBinary === 'object') {
            console.log('[HEIC] findEXIFinHEIC keys:', Object.keys(exifBinary));
          }
          console.log('[HEIC] findEXIFinHEIC result:', {
            type: exifBinary ? Object.prototype.toString.call(exifBinary) : 'null',
            byteLength: exifBinary && exifBinary.byteLength ? exifBinary.byteLength : 0
          });
          if (exifBinary && typeof exifBinary === 'object' && !exifBinary.byteLength) {
            exifObject = exifBinary;
          }
          exifBinary = this._normalizeExifBinary(exifBinary);
          if (!exifBinary && exifObject && window.piexif) {
            exifStrFromObject = this._buildExifStrFromObject(exifObject);
          }
          if (exifBinary) console.log('HEICからEXIFを抽出しました');
        }
      } catch (e) {
        console.warn('EXIFの抽出に失敗しました（無視して続行）:', e);
      }

      // 2. タイムアウト付きで変換を実行
      const convertedBlob = await this._convertWithTimeout(heicFile, quality);
      
      // 3. 変換されたBlobをEXIFとともに処理
      let finalBlob = convertedBlob;
      const piexif = window.piexif;

      if ((exifBinary || exifStrFromObject) && piexif) {
        try {
          // Blobをバイナリ文字列に変換
          const jpegBinary = await this._blobToBinaryString(convertedBlob);
          
          // EXIFを再注入
          // findEXIFinHEICが返すのは 'Exif\0\0' で始まるTIFF形式の可能性が高い
          let exifStr = exifStrFromObject || this._arrayBufferToBinaryString(exifBinary);
          if (exifStr && exifStr.substring(0, 4) !== 'Exif') {
            console.log('[HEIC] "Exif" header missing, prepending "Exif\\0\\0"');
            exifStr = '\x45\x78\x69\x66\x00\x00' + exifStr;
          }
          if (!exifStr) {
            throw new Error('EXIF string is empty');
          }
          const insertedBinary = piexif.insert(exifStr, jpegBinary);
          
          // 再びBlobに戻す
          finalBlob = this._binaryStringToBlob(insertedBinary, 'image/jpeg');
          console.log('変換後のJPEGにEXIFを再注入しました');
        } catch (e) {
          console.warn('EXIFの再注入に失敗しました:', e);
        }
      }
      
      // 変換されたBlobをFileオブジェクトに変換
      const jpegFileName = heicFile.name.replace(/\.(heic|heif)$/i, '.jpg');
      const jpegFile = new File([finalBlob], jpegFileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      if (exifBinary || exifStrFromObject) {
        try {
          const exifMap = window.__heicExifMap || (window.__heicExifMap = new WeakMap());
          exifMap.set(jpegFile, { exifBinary, exifStr: exifStrFromObject });
        } catch (e) {
          // Fallback for environments where WeakMap is not available
          if (exifBinary) {
            jpegFile._exifBinary = exifBinary;
          } else if (exifStrFromObject) {
            jpegFile._exifStr = exifStrFromObject;
          }
        }
      }

      console.log('HEIC変換成功:', jpegFileName, `(${Math.round(jpegFile.size / 1024)}KB)`);
      this.lastConversionMethod = 'client';
      return jpegFile;

    } catch (error) {
      console.error('HEIC変換エラー:', error);
      
      // エラーメッセージの整理
      let errorMessage = error.message || 'Unknown error';
      if (errorMessage.includes('timeout')) {
        throw new Error(`HEIC変換がタイムアウトしました(30秒): ${heicFile.name}`);
      } else {
        throw new Error(`画像形式の変換に失敗しました(heic2any): ${errorMessage}`);
      }
    }
  }

  /**
   * タイムアウト機能付きの変換処理
   * @private
   */
  async _convertWithTimeout(heicFile, quality) {
    return new Promise((resolve, reject) => {
      // タイムアウト設定
      const timeoutId = setTimeout(() => {
        reject(new Error('HEIC conversion timeout'));
      }, this.conversionTimeout);

      // heic2anyで変換実行
      heic2any({
        blob: heicFile,
        toType: 'image/jpeg',
        quality: quality,
        multiple: true
      })
        .then(async convertedBlob => {
          clearTimeout(timeoutId);
          try {
            const selectedBlob = await this._selectLargestBlob(convertedBlob);
            resolve(selectedBlob);
          } catch (selectError) {
            reject(selectError);
          }
        })
        .catch(error => {
          clearTimeout(timeoutId);
          reject(error);
        });
    });
  }

  /**
   * HEIC/HEIF画像をJPEGに変換（サーバーフォールバック付き）
   * クライアント側で変換を試み、失敗したらサーバー側で変換
   * iOS 18以降のHEICファイルに対応
   * 
   * @param {File} heicFile - 変換するHEIC/HEIFファイル
   * @param {Object} options - 変換オプション
   * @param {number} options.quality - JPEG品質 (0-1, デフォルト: 0.8)
   * @returns {Promise<File>} 変換されたJPEGファイル
   */
  async convertToJPEGWithFallback(heicFile, options = { quality: 0.8 }) {
    const forceServer = options.forceServer === true;
    // 1. まずEXIFを抽出しておく（クライアント変換失敗に備えて退避）
    let exifBinary = null;
    let exifObject = null;
    let exifStrFromObject = null;
    try {
      await this.loadExifLib();
      console.log('[HEIC] findEXIFinHEIC availability:', typeof window.findEXIFinHEIC);
      if (window.findEXIFinHEIC) {
        const buffer = await heicFile.arrayBuffer();
        exifBinary = window.findEXIFinHEIC(buffer);
        if (exifBinary && typeof exifBinary === 'object') {
          console.log('[HEIC] findEXIFinHEIC keys:', Object.keys(exifBinary));
        }
        console.log('[HEIC] findEXIFinHEIC result:', {
          type: exifBinary ? Object.prototype.toString.call(exifBinary) : 'null',
          byteLength: exifBinary && exifBinary.byteLength ? exifBinary.byteLength : 0
        });
        if (exifBinary && typeof exifBinary === 'object' && !exifBinary.byteLength) {
          exifObject = exifBinary;
        }
        exifBinary = this._normalizeExifBinary(exifBinary);
        if (!exifBinary && exifObject && window.piexif) {
          exifStrFromObject = this._buildExifStrFromObject(exifObject);
        }
        if (exifBinary) console.log('[HEIC] EXIFデータを抽出しました（バックアップ用）');
      }
    } catch (e) {
      console.warn('[HEIC] EXIFの抽出に失敗しました（無視して続行）:', e);
    }
    let exifStrForServer = exifStrFromObject || (exifBinary ? this._arrayBufferToBinaryString(exifBinary) : null);
    if (exifStrForServer && exifStrForServer.substring(0, 4) !== 'Exif') {
      exifStrForServer = '\x45\x78\x69\x66\x00\x00' + exifStrForServer;
    }

    try {
      if (forceServer) {
        throw new Error('Force server conversion');
      }

      // 2. クライアント側で変換を試行
      console.log('[HEIC] クライアント側で変換を試行します:', heicFile.name);
      const clientConverted = await this.convertToJPEG(heicFile, options);
      if (exifBinary && window.piexif) {
        try {
          const clientBinary = await this._blobToBinaryString(clientConverted);
          const clientExif = window.piexif.load(clientBinary);
          const gps = clientExif && clientExif.GPS ? clientExif.GPS : null;
          if (!gps || Object.keys(gps).length === 0) {
            throw new Error('Client conversion missing GPS EXIF');
          }
        } catch (exifCheckError) {
          console.warn('[HEIC] クライアント変換後のGPS EXIFが欠落しています。サーバー変換へフォールバックします:', exifCheckError);
          throw exifCheckError;
        }
      }
      const shouldFallback = await this._isLikelyThumbnailResult(heicFile, clientConverted);
      if (shouldFallback) {
        throw new Error('Client conversion returned a thumbnail-sized image');
      }
      return clientConverted;
    } catch (error) {
      console.warn('[HEIC] クライアント側変換失敗、サーバー側で変換します:', error.message);
      
      // 3. サーバー側変換にフォールバック
      try {
        const serverConvertedBlob = await this._convertOnServer(heicFile, {
          ...options,
          exifStr: exifStrForServer
        });
        if (serverConvertedBlob && serverConvertedBlob.serverUploadResult) {
          return serverConvertedBlob;
        }
        
        // 4. サーバー変換後のJPEGにEXIFを再注入
        // 調査完了: EXIF注入処理自体は画角トリミングの原因ではないことが判明したため有効化
        if ((exifBinary || exifStrFromObject) && window.piexif) {
            try {
                const jpegBinary = await this._blobToBinaryString(serverConvertedBlob);
                let exifStr = exifStrFromObject || this._arrayBufferToBinaryString(exifBinary);

                // --- DEBUG LOG START ---
                // EXIFデータの先頭20バイトをHEXで表示して構造を確認する
                let hexLog = '';
                const debugBytes = exifBinary ? new Uint8Array(exifBinary.slice(0, 20)) : new Uint8Array();
                debugBytes.forEach(b => { hexLog += b.toString(16).padStart(2, '0') + ' '; });
                console.log(`[HEIC] Extracted EXIF Header (Hex): ${hexLog}`);
                console.log(`[HEIC] Extracted EXIF Length: ${exifBinary ? exifBinary.byteLength : 0}`);
                // --- DEBUG LOG END ---
                
                // piexif.insertは "Exif" で始まるデータを期待するため、必要に応じて補完
                if (exifStr && exifStr.substring(0, 4) !== 'Exif') {
                    console.log('[HEIC] "Exif" header missing, prepending "Exif\\0\\0"');
                    // "Exif\0\0" ヘッダを付与
                    exifStr = '\x45\x78\x69\x66\x00\x00' + exifStr;
                } else {
                    console.log('[HEIC] "Exif" header found in extracted data');
                }
                if (!exifStr) {
                  throw new Error('EXIF string is empty');
                }
                
                // insert実行
                const insertedBinary = window.piexif.insert(exifStr, jpegBinary);
                
                // 成功確認（insert結果の先頭を確認しても良いが、長さが変わっているはず）
                console.log(`[HEIC] EXIF insertion complete. Original JPEG len: ${jpegBinary.length}, New len: ${insertedBinary.length}`);

                const finalBlob = this._binaryStringToBlob(insertedBinary, 'image/jpeg');
                
                // Fileオブジェクトを再作成
                const jpegFileName = heicFile.name.replace(/\.(heic|heif)$/i, '.jpg');
                const finalFile = new File([finalBlob], jpegFileName, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });
                if (exifBinary || exifStrFromObject) {
                  try {
                    const exifMap = window.__heicExifMap || (window.__heicExifMap = new WeakMap());
                    exifMap.set(finalFile, { exifBinary, exifStr: exifStrFromObject });
                  } catch (e) {
                    if (exifBinary) {
                      finalFile._exifBinary = exifBinary;
                    } else if (exifStrFromObject) {
                      finalFile._exifStr = exifStrFromObject;
                    }
                  }
                }
                console.log('[HEIC] サーバー変換後の画像にEXIFを再注入しました');
                this.lastConversionMethod = 'server';
                return finalFile;
            } catch (exifError) {
                console.warn('[HEIC] サーバー変換後画像のEXIF再注入に失敗しました:', exifError);
                // 失敗した場合はサーバーから返ってきた画像をそのまま使う
                // (サーバー側でEXIFが保持されている可能性もあるため)
                
                // BlobからFileへ変換
                const jpegFileName = heicFile.name.replace(/\.(heic|heif)$/i, '.jpg');
                this.lastConversionMethod = 'server';
                const fallbackFile = new File([serverConvertedBlob], jpegFileName, { type: 'image/jpeg', lastModified: Date.now() });
                if (exifBinary || exifStrFromObject) {
                  try {
                    const exifMap = window.__heicExifMap || (window.__heicExifMap = new WeakMap());
                    exifMap.set(fallbackFile, { exifBinary, exifStr: exifStrFromObject });
                  } catch (e) {
                    if (exifBinary) {
                      fallbackFile._exifBinary = exifBinary;
                    } else if (exifStrFromObject) {
                      fallbackFile._exifStr = exifStrFromObject;
                    }
                  }
                }
                return fallbackFile;
            }
        }
        
        // EXIFがない、または注入失敗時はそのままFile化して返す
        const jpegFileName = heicFile.name.replace(/\.(heic|heif)$/i, '.jpg');
        this.lastConversionMethod = 'server';
        const plainFile = new File([serverConvertedBlob], jpegFileName, { type: 'image/jpeg', lastModified: Date.now() });
        if (exifBinary || exifStrFromObject) {
          try {
            const exifMap = window.__heicExifMap || (window.__heicExifMap = new WeakMap());
            exifMap.set(plainFile, { exifBinary, exifStr: exifStrFromObject });
          } catch (e) {
            if (exifBinary) {
              plainFile._exifBinary = exifBinary;
            } else if (exifStrFromObject) {
              plainFile._exifStr = exifStrFromObject;
            }
          }
        }
        return plainFile;

      } catch (serverError) {
        console.error('[HEIC] サーバー側変換も失敗しました:', serverError);
        throw new Error(`HEIC変換に失敗しました: ${heicFile.name} (クライアント: ${error.message}, サーバー: ${serverError.message})`);
      }
    }
  }

  /**
   * サーバー側でHEIC→JPEG変換を実行
   * @private
   */
  async _convertOnServer(heicFile, options = { quality: 0.8 }) {
    if (!this._serverConvertQueue) {
      this._serverConvertQueue = Promise.resolve();
    }
    const runTask = () => this._convertOnServerInternal(heicFile, options);
    const task = this._serverConvertQueue.then(runTask, runTask);
    this._serverConvertQueue = task.catch(() => {});
    return task;
  }

  async _convertOnServerInternal(heicFile, options = { quality: 0.8 }) {
    const quality = options.quality || 0.8;
    const exifStr = options.exifStr || null;
    const serverUpload = options.serverUpload === true;
    const uploadOptions = options.uploadOptions || {};
    
    console.log('[HEIC] サーバー側変換API呼び出し:', heicFile.name);
    
    const formData = new FormData();
    formData.append('heic_file', heicFile);
    formData.append('quality', String(Math.round(quality * 100)));
    if (exifStr) {
      formData.append('exif_base64', this._binaryStringToBase64(exifStr));
    }
    if (serverUpload) {
      formData.append('server_upload', '1');
      if (options.webUploaderUrl) {
        formData.append('web_uploader_url', options.webUploaderUrl);
      }
      Object.entries(uploadOptions).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'function') return;
        if (typeof value === 'object') return;
        formData.append(key, String(value));
      });
    }
    
    let uploadUrl = '/api/convert-heic.php';
    if (window.AS && window.AS.domains) {
        let domain = window.AS.domains.api || window.AS.domains.image || '';
        
        // PHPスクリプト（.php）の場合、ポート3000（Node.js/Hapi）を避ける必要がある
        // ポート3000が含まれている場合は、現在のオリジン（通常はポート443のPHPサーバー）を使用する
        if (domain.includes(':3000')) {
            uploadUrl = window.location.origin + '/api/convert-heic.php';
        } else if (domain) {
            uploadUrl = domain + '/api/convert-heic.php';
        }
    }

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.debug) {
        console.error('[HEIC] Server debug info:', errorData.debug);
      }
      throw new Error(errorData.error || 'Server conversion failed');
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Conversion failed');
    }
    if (result.server_upload) {
      return { serverUploadResult: result };
    }
    
    console.log(`[HEIC] サーバー側変換成功 (${result.method}):`, result.filename, `(${Math.round(result.size / 1024)}KB)`);
    
    // Base64からBlobに変換
    const binaryString = atob(result.data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return new File([bytes], result.filename, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  }

  async _selectLargestBlob(convertedBlob) {
    if (!Array.isArray(convertedBlob)) {
      return convertedBlob;
    }

    if (convertedBlob.length === 0) {
      throw new Error('HEIC conversion returned no images');
    }

    if (convertedBlob.length === 1) {
      return convertedBlob[0];
    }

    const dimensions = await Promise.all(
      convertedBlob.map((blob) => this._getImageDimensionsFromBlob(blob))
    );

    let bestIndex = 0;
    let bestPixels = 0;

    dimensions.forEach((size, index) => {
      const pixels = (size.width || 0) * (size.height || 0);
      if (pixels > bestPixels) {
        bestPixels = pixels;
        bestIndex = index;
      }
    });

    if (bestPixels === 0) {
      let bestBlob = convertedBlob[0];
      convertedBlob.forEach((blob) => {
        if (blob.size > bestBlob.size) {
          bestBlob = blob;
        }
      });
      return bestBlob;
    }

    return convertedBlob[bestIndex];
  }

  _getImageDimensionsFromBlob(blob) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth || img.width || 0,
          height: img.naturalHeight || img.height || 0
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };

      img.src = url;
    });
  }

  // ユーティリティ
  _blobToBinaryString(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsBinaryString(blob);
    });
  }

  _arrayBufferToBinaryString(buffer) {
    const normalized = this._normalizeExifBinary(buffer);
    if (!normalized) {
      return '';
    }
    let binary = '';
    const bytes = new Uint8Array(normalized);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return binary;
  }

  _normalizeExifBinary(exifBinary) {
    if (!exifBinary) {
      return null;
    }
    if (exifBinary instanceof ArrayBuffer) {
      return exifBinary;
    }
    if (ArrayBuffer.isView(exifBinary)) {
      return exifBinary.buffer.slice(exifBinary.byteOffset, exifBinary.byteOffset + exifBinary.byteLength);
    }
    if (typeof exifBinary === 'object') {
      const candidates = [
        exifBinary.exif,
        exifBinary.exifBuffer,
        exifBinary.exifData,
        exifBinary.tiff,
        exifBinary.data,
        exifBinary.buffer,
        exifBinary.raw
      ];
      for (const candidate of candidates) {
        const normalized = this._normalizeExifBinary(candidate);
        if (normalized) {
          return normalized;
        }
      }
      if (typeof exifBinary.toArrayBuffer === 'function') {
        try {
          const buffer = exifBinary.toArrayBuffer();
          return this._normalizeExifBinary(buffer);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  }

  _buildExifStrFromObject(exifObject) {
    if (!exifObject || !window.piexif) {
      return null;
    }
    const piexif = window.piexif;
    const exif = { "0th": {}, "Exif": {}, "GPS": {} };
    const assign = (ifd, tag, value, transform) => {
      if (value === undefined || value === null || value === '') return;
      const v = transform ? transform(value) : value;
      if (v === undefined || v === null || v === '') return;
      exif[ifd][tag] = v;
    };
    const toRational = (val) => {
      if (Array.isArray(val) && val.length === 2 && typeof val[0] === 'number' && typeof val[1] === 'number') {
        return [val[0], val[1]];
      }
      if (val && typeof val === 'object') {
        const num = val.numerator ?? val.num ?? val[0];
        const den = val.denominator ?? val.den ?? val[1];
        if (typeof num === 'number' && typeof den === 'number' && den !== 0) {
          return [num, den];
        }
      }
      if (typeof val === 'string' && val.includes('/')) {
        const parts = val.split('/');
        const num = Number(parts[0]);
        const den = Number(parts[1]);
        if (!Number.isNaN(num) && !Number.isNaN(den) && den !== 0) {
          return [num, den];
        }
      }
      if (typeof val === 'number') {
        return [Math.round(val * 1000000), 1000000];
      }
      return null;
    };
    const parseDmsString = (val) => {
      if (typeof val !== 'string') return null;
      const parts = val.split(/[,\\s]+/).filter(Boolean);
      if (parts.length === 0) return null;
      const out = parts.map((p) => {
        if (p.includes('/')) {
          const [a, b] = p.split('/');
          const num = Number(a);
          const den = Number(b);
          if (!Number.isNaN(num) && !Number.isNaN(den) && den !== 0) {
            return [num, den];
          }
        }
        const num = Number(p);
        if (!Number.isNaN(num)) {
          return [Math.round(num * 1000000), 1000000];
        }
        return null;
      }).filter(Boolean);
      return out.length ? out : null;
    };
    const toRationalArray = (val) => {
      if (Array.isArray(val)) {
        return val.map(v => toRational(v)).filter(Boolean);
      }
      if (typeof val === 'string') {
        return parseDmsString(val);
      }
      if (typeof val === 'number') {
        return [
          [Math.floor(val), 1],
          [Math.floor((val % 1) * 60), 1],
          [Math.round((((val % 1) * 60) % 1) * 60 * 1000000), 1000000]
        ];
      }
      return null;
    };
    const asString = (val) => (val === undefined || val === null) ? null : String(val);

    // Minimal stable tags to avoid piexif pack errors
    assign("0th", piexif.ImageIFD.Make, asString(exifObject.Make));
    assign("0th", piexif.ImageIFD.Model, asString(exifObject.Model));
    // Canvas export is already visually oriented; keep Orientation=1 to avoid double-rotation
    assign("0th", piexif.ImageIFD.Orientation, 1);
    assign("0th", piexif.ImageIFD.DateTime, asString(exifObject.DateTime));
    assign("Exif", piexif.ExifIFD.DateTimeOriginal, asString(exifObject.DateTimeOriginal));
    assign("Exif", piexif.ExifIFD.DateTimeDigitized, asString(exifObject.DateTimeDigitized));

    assign("GPS", piexif.GPSIFD.GPSLatitudeRef, asString(exifObject.GPSLatitudeRef));
    assign("GPS", piexif.GPSIFD.GPSLatitude, toRationalArray(exifObject.GPSLatitude));
    assign("GPS", piexif.GPSIFD.GPSLongitudeRef, asString(exifObject.GPSLongitudeRef));
    assign("GPS", piexif.GPSIFD.GPSLongitude, toRationalArray(exifObject.GPSLongitude));
    assign("GPS", piexif.GPSIFD.GPSAltitudeRef, Number(exifObject.GPSAltitudeRef) || 0);
    assign("GPS", piexif.GPSIFD.GPSAltitude, toRational(exifObject.GPSAltitude));
    assign("GPS", piexif.GPSIFD.GPSDateStamp, asString(exifObject.GPSDateStamp));

    try {
      return piexif.dump(exif);
    } catch (e) {
      console.warn('[HEIC] piexif.dump failed for exifObject:', e);
      return null;
    }
  }

  _binaryStringToBlob(binaryString, mimeType) {
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  _binaryStringToBase64(binaryString) {
    return btoa(binaryString);
  }

  async _isLikelyThumbnailResult(heicFile, jpegFile) {
    const dimensions = await this._getImageDimensionsFromBlob(jpegFile);
    const maxDim = Math.max(dimensions.width || 0, dimensions.height || 0);
    const minHeicBytes = 2 * 1024 * 1024;
    const maxJpegBytes = 350 * 1024;

    if (maxDim === 0) {
      return false;
    }

    if (maxDim <= 1600 && (heicFile.size >= minHeicBytes || jpegFile.size <= maxJpegBytes)) {
      console.warn('[HEIC] クライアント変換結果がサムネイルサイズの可能性があるため、サーバー変換へフォールバックします:', {
        heicBytes: heicFile.size,
        jpegBytes: jpegFile.size,
        width: dimensions.width,
        height: dimensions.height
      });
      return true;
    }

    return false;
  }
}

// シングルトンインスタンスをエクスポート
const heicSupport = new HEICSupport();
window.heicSupport = heicSupport;

export default heicSupport;
